import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import { frequencyLabel, isOffice365, formatAUD, monthlyEquivalent, monthlyEquivalentAmount, serviceRenewsInMonth, CHART_COLORS } from "../../lib/billing";
import { DollarSign, CalendarClock, Layers, Zap, Monitor, Wallet, TrendingUp } from "lucide-react";

const FREQUENCY_BADGE_STYLES = {
  monthly: "bg-blue-100 text-blue-700",
  quarterly: "bg-cyan-100 text-cyan-700",
  yearly: "bg-violet-100 text-violet-700",
  one_off: "bg-amber-100 text-amber-700",
};

function StatCard({ icon: Icon, label, value, accent, testId }) {
  return (
    <Card data-testid={testId}>
      <CardContent className="pt-5 pb-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${accent}`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-xs text-slate-500">{label}</p>
            <p className="text-xl font-bold text-slate-900">{value}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function BillingProfileSummary({ customer, profile }) {
  const services = profile.services || [];
  const monthlyRecurring = services.reduce((sum, s) => sum + monthlyEquivalent(s), 0);
  const annualValue = monthlyRecurring * 12;
  const monthlyCost = services.reduce(
    (sum, s) => sum + monthlyEquivalentAmount(s.ots_cost, s.cost_frequency || "monthly"),
    0
  );
  const monthlyProfit = monthlyRecurring - monthlyCost;
  const oneOffTotal = services
    .filter((s) => s.frequency === "one_off")
    .reduce((sum, s) => sum + (parseFloat(s.price) || 0), 0);

  const pieData = services
    .map((s) => ({ name: s.service_name, value: Math.round(monthlyEquivalent(s) * 100) / 100 }))
    .filter((d) => d.value > 0);

  const freqData = ["monthly", "quarterly", "yearly", "one_off"]
    .map((f) => ({
      name: frequencyLabel(f),
      amount: services.filter((s) => s.frequency === f).reduce((sum, s) => sum + (parseFloat(s.price) || 0), 0),
    }))
    .filter((d) => d.amount > 0);

  const now = new Date();
  const renewalChecks = services.map((s) => ({ svc: s, ...serviceRenewsInMonth(s, now.getFullYear(), now.getMonth() + 1) }));
  const renewingNow = renewalChecks.filter((r) => r.renews);
  const missingDates = renewalChecks.filter((r) => r.needsDate);
  const renewingTotal = renewingNow.reduce((sum, r) => sum + (parseFloat(r.svc.price) || 0), 0);

  return (
    <div className="space-y-6" data-testid="billing-profile-summary">
      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard icon={DollarSign} label="Monthly Recurring" value={formatAUD(monthlyRecurring)} accent="bg-blue-600" testId="summary-stat-monthly" />
        <StatCard icon={Wallet} label="Monthly OTS Cost" value={formatAUD(monthlyCost)} accent="bg-slate-600" testId="summary-stat-cost" />
        <StatCard icon={TrendingUp} label="Monthly Profit" value={formatAUD(monthlyProfit)} accent={monthlyProfit >= 0 ? "bg-emerald-600" : "bg-red-600"} testId="summary-stat-profit" />
        <StatCard icon={CalendarClock} label="Annual Value" value={formatAUD(annualValue)} accent="bg-cyan-600" testId="summary-stat-annual" />
        <StatCard icon={Zap} label="One-off Charges" value={formatAUD(oneOffTotal)} accent="bg-amber-500" testId="summary-stat-oneoff" />
        <StatCard icon={Layers} label="Services" value={services.length} accent="bg-violet-600" testId="summary-stat-services" />
      </div>

      {/* Renewing this month */}
      <Card className="border-l-4 border-l-cyan-500" data-testid="client-renewals-this-month">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <CalendarClock className="w-4 h-4 text-cyan-600" />
            Renewing in {new Date().toLocaleString("en-AU", { month: "long", year: "numeric" })}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-1.5">
          {renewingNow.length === 0 ? (
            <p className="text-sm text-slate-500">No services renewing this month.</p>
          ) : (
            <>
              {renewingNow.map((r, i) => (
                <div key={i} className="flex items-center justify-between text-sm" data-testid={`client-renewal-row-${i}`}>
                  <span className="text-slate-700 flex items-center gap-2">
                    {r.svc.service_name}
                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0">{frequencyLabel(r.svc.frequency)}</Badge>
                    {r.day && <span className="text-xs text-slate-400">on the {r.day}</span>}
                  </span>
                  <span className="font-semibold text-slate-900">{formatAUD(r.svc.price)}</span>
                </div>
              ))}
              <div className="flex items-center justify-between pt-1.5 border-t text-sm font-bold" data-testid="client-renewal-total">
                <span className="text-slate-600">{renewingNow.length} renewal{renewingNow.length !== 1 ? "s" : ""} this month</span>
                <span className="text-cyan-700">{formatAUD(renewingTotal)}</span>
              </div>
            </>
          )}
          {missingDates.length > 0 && (
            <p className="text-xs text-amber-600 pt-1">
              {missingDates.map((r) => r.svc.service_name).join(", ")}: no renewal date set — edit the profile to include them in renewal tracking.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Charts */}
      {(pieData.length > 0 || freqData.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {pieData.length > 0 && (
            <Card data-testid="summary-pie-chart">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Monthly Revenue by Service</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3}>
                      {pieData.map((entry, i) => (
                        <Cell key={entry.name} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => formatAUD(v)} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
          {freqData.length > 0 && (
            <Card data-testid="summary-bar-chart">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Charges by Billing Frequency</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={freqData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${v}`} />
                    <Tooltip formatter={(v) => formatAUD(v)} />
                    <Bar dataKey="amount" fill="#2563eb" radius={[6, 6, 0, 0]} maxBarSize={60} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Service cards */}
      <div>
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Subscribed Services</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {services.map((svc, idx) => {
            const o365 = isOffice365(svc.service_name);
            const monthly = monthlyEquivalent(svc);
            const monthlyServiceCost = monthlyEquivalentAmount(svc.ots_cost, svc.cost_frequency || "monthly");
            const monthlyServiceProfit = monthly - monthlyServiceCost;
            return (
              <Card key={`${svc.service_name}-${idx}`} className="border-slate-200 hover:shadow-md transition-shadow" data-testid={`summary-service-card-${idx}`}>
                <CardHeader className="pb-2 flex flex-row items-start justify-between space-y-0">
                  <CardTitle className="text-base flex items-center gap-2">
                    {o365 && <Monitor className="w-4 h-4 text-blue-600" />}
                    {svc.service_name}
                  </CardTitle>
                  <Badge className={`${FREQUENCY_BADGE_STYLES[svc.frequency] || "bg-slate-100 text-slate-700"} hover:bg-opacity-100`}>
                    {frequencyLabel(svc.frequency)}
                  </Badge>
                </CardHeader>
                <CardContent className="space-y-2">
                  <p className="text-2xl font-bold text-slate-900">
                    {formatAUD(svc.price)}
                    <span className="text-sm font-normal text-slate-500 ml-1">
                      / {svc.frequency === "one_off" ? "one-off" : frequencyLabel(svc.frequency).toLowerCase()}
                    </span>
                  </p>
                  {(parseFloat(svc.quantity) || 1) > 1 && (
                    <p className="text-xs text-slate-500" data-testid={`summary-service-qty-${idx}`}>
                      {parseFloat(svc.quantity)} × {formatAUD(svc.unit_price ?? svc.price / (parseFloat(svc.quantity) || 1))} each
                    </p>
                  )}
                  {svc.frequency !== "monthly" && svc.frequency !== "one_off" && (
                    <p className="text-xs text-slate-500">≈ {formatAUD(monthly)} / month</p>
                  )}
                  <div className="pt-2 border-t border-slate-100 space-y-1">
                    <div className="flex justify-between text-xs text-slate-600">
                      <span>OTS Cost</span>
                      <span>
                        {formatAUD(svc.ots_cost)} / {frequencyLabel(svc.cost_frequency || "monthly").toLowerCase()}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs font-semibold">
                      <span className="text-slate-600">Profit / month</span>
                      <span className={monthlyServiceProfit >= 0 ? "text-emerald-600" : "text-red-600"} data-testid={`summary-service-profit-${idx}`}>
                        {formatAUD(monthlyServiceProfit)}
                      </span>
                    </div>
                  </div>
                  {svc.licenses && svc.licenses.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-slate-100 space-y-1">
                      <p className="text-xs font-semibold text-slate-600">Licenses</p>
                      {svc.licenses.map((lic, lIdx) => (
                        <div key={lIdx} className="flex justify-between text-xs text-slate-600">
                          <span>{lic.license_type_name} × {lic.quantity}</span>
                          <span className="font-medium">{formatAUD(lic.quantity * lic.price_per_seat)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}
